
import React, { useEffect, useState, useCallback } from "react";
import {
  Row, Col, Button, notification, Form, Input, Select, DatePicker,
  InputNumber, Upload, Divider, Typography, Modal, Alert, Progress, Spin,
} from "antd";
import axios from "axios";
import {
  SaveOutlined, UploadOutlined, BankOutlined, PlusOutlined,
  DeleteOutlined, SettingOutlined, PrinterOutlined, PictureOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { baseUrl } from "../../config";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const DEFAULT_ORG = {
  name: 'Shri Kheteshwar Brahmdham Tirth, Asotra',
  subtitle: 'ABRSVS – Samaj Shiksha Nidhi Samarpan Yojana',
  address: '',
  phone: '',
  email: '',
  gst: '',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const inr = (v) =>
  '₹ ' + Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const newItem = (key) => ({ key, description: '', qty: 1, rate: 0, taxPct: 0 });

function computeTotals(items) {
  let subtotal = 0, taxTotal = 0;
  items.forEach(item => {
    const s = (item.qty || 0) * (item.rate || 0);
    subtotal += s;
    taxTotal += s * (item.taxPct || 0) / 100;
  });
  return { subtotal, taxTotal, grandTotal: subtotal + taxTotal };
}

// ─── Live Invoice Preview ──────────────────────────────────────────────
function InvoicePreview({ org, values, items, beneficiariesList, purposesList }) {
  const { subtotal, taxTotal, grandTotal } = computeTotals(items);
  const ben = beneficiariesList.find(b => b.id === values.beneficiary_id);
  const pur = purposesList.find(p => p.id === values.purpose_id);
  const date = values.txn_date ? moment(values.txn_date).format('DD MMMM YYYY') : '—';

  const secTitle = (label) => (
    <div style={{
      fontSize: 9, fontWeight: 700, color: '#7a0000',
      textTransform: 'uppercase', letterSpacing: 0.8,
      borderLeft: '3px solid #7a0000', paddingLeft: 6, marginBottom: 7
    }}>{label}</div>
  );

  return (
    <div style={{
      fontFamily: "'Segoe UI', Arial, sans-serif",
      fontSize: 11, color: '#333', background: '#fff',
      padding: 18, border: '1px solid #dde3ed',
      borderRadius: '0 0 8px 8px', minHeight: 600,
    }}>
      {/* Org Header */}
      <div style={{ textAlign: 'center', paddingBottom: 12, borderBottom: '3px solid #7a0000', marginBottom: 14 }}>
        {org.logo_url && (
          <img
            src={org.logo_url}
            alt="logo"
            style={{ height: 56, width: 'auto', objectFit: 'contain', marginBottom: 6, display: 'block', margin: '0 auto 6px' }}
          />
        )}
        <div style={{ fontSize: 15, fontWeight: 800, color: '#7a0000' }}>
          {org.name || 'Organization Name'}
        </div>
        {org.subtitle && (
          <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>{org.subtitle}</div>
        )}
        {org.address && (
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>{org.address}</div>
        )}
        {(org.phone || org.email) && (
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
            {org.phone && `Ph: ${org.phone}`}
            {org.phone && org.email ? '  |  ' : ''}
            {org.email}
          </div>
        )}
        {org.gst && (
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>GSTIN: {org.gst}</div>
        )}
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 3, color: '#7a0000', marginTop: 8 }}>
          EXPENDITURE VOUCHER
        </div>
      </div>

      {/* Meta Cards */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          ['Invoice No.', values.invoice_no || 'AUTO'],
          ['Date', date],
          ['Payment Mode', values.payment_mode || '—'],
          ['Status', values.approval_status || 'Pending'],
        ].map(([label, val]) => (
          <div key={label} style={{
            flex: 1, minWidth: 80,
            background: '#f7f9fc', border: '1px solid #dde3ed',
            borderRadius: 5, padding: '6px 8px'
          }}>
            <div style={{ fontSize: 8, color: '#999', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', marginTop: 2 }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Beneficiary & Purpose */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, border: '1px solid #eee', borderRadius: 5, padding: '7px 10px' }}>
          {secTitle('Beneficiary')}
          <div style={{ fontWeight: 600, fontSize: 11 }}>{ben?.name || '—'}</div>
          <div style={{ color: '#888', fontSize: 9, marginTop: 2 }}>{ben?.type || ''}</div>
          {ben?.phone && <div style={{ color: '#888', fontSize: 9 }}>{ben.phone}</div>}
        </div>
        <div style={{ flex: 1, border: '1px solid #eee', borderRadius: 5, padding: '7px 10px' }}>
          {secTitle('Purpose')}
          <div style={{ fontWeight: 600, fontSize: 11 }}>{pur?.name || '—'}</div>
          <div style={{ color: '#888', fontSize: 9, marginTop: 2 }}>{pur?.code || ''}</div>
          {pur?.category && <div style={{ color: '#888', fontSize: 9 }}>{pur.category}</div>}
        </div>
      </div>

      {/* Line Items Table */}
      <div style={{ marginBottom: 12 }}>
        {secTitle('Items')}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead>
            <tr style={{ background: '#7a0000', color: '#fff' }}>
              {['#', 'Description', 'Qty', 'Rate', 'Tax%', 'Amount'].map((h, i) => (
                <th key={h} style={{
                  padding: '5px 7px',
                  textAlign: i >= 2 ? 'right' : 'left',
                  fontWeight: 700, fontSize: 10
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '10px', textAlign: 'center', color: '#bbb', fontSize: 10 }}>
                  No items added
                </td>
              </tr>
            ) : items.map((item, idx) => {
              const s = (item.qty || 0) * (item.rate || 0);
              const t = s * (item.taxPct || 0) / 100;
              return (
                <tr key={item.key} style={{ background: idx % 2 === 0 ? '#fff' : '#f7f9fc' }}>
                  <td style={{ padding: '4px 7px', borderBottom: '1px solid #f0f0f0' }}>{idx + 1}</td>
                  <td style={{ padding: '4px 7px', borderBottom: '1px solid #f0f0f0' }}>{item.description || '—'}</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{item.qty}</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{inr(item.rate)}</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{item.taxPct}%</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{inr(s + t)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ padding: '4px 7px', textAlign: 'right', color: '#666', borderTop: '1px solid #dde3ed' }}>Subtotal</td>
              <td style={{ padding: '4px 7px', textAlign: 'right', borderTop: '1px solid #dde3ed', fontWeight: 600 }}>{inr(subtotal)}</td>
            </tr>
            <tr>
              <td colSpan={5} style={{ padding: '4px 7px', textAlign: 'right', color: '#666' }}>Tax</td>
              <td style={{ padding: '4px 7px', textAlign: 'right', fontWeight: 600 }}>{inr(taxTotal)}</td>
            </tr>
            <tr style={{ background: '#7a0000', color: '#fff' }}>
              <td colSpan={5} style={{ padding: '7px', textAlign: 'right', fontWeight: 700, fontSize: 11 }}>GRAND TOTAL</td>
              <td style={{ padding: '7px', textAlign: 'right', fontWeight: 800, fontSize: 12 }}>{inr(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Payment Info */}
      {(values.utr_no || values.cheque_no || values.transaction_ref) && (
        <div style={{ marginBottom: 10, border: '1px solid #eee', borderRadius: 5, padding: '7px 10px', fontSize: 10 }}>
          {secTitle('Payment Info')}
          {values.utr_no && <div>UTR: <b>{values.utr_no}</b></div>}
          {values.cheque_no && <div>Cheque: <b>{values.cheque_no}</b></div>}
          {values.transaction_ref && <div>Ref: <b>{values.transaction_ref}</b></div>}
        </div>
      )}

      {/* Remarks */}
      {values.remarks && (
        <div style={{ marginBottom: 10, fontSize: 10, fontStyle: 'italic', color: '#666', borderLeft: '3px solid #dde3ed', paddingLeft: 8 }}>
          Remarks: {values.remarks}
        </div>
      )}

      {/* Signature */}
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #999', paddingTop: 5, minWidth: 150, fontSize: 9, color: '#777' }}>
            Authorized Signatory
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #eee', fontSize: 8, color: '#ccc', textAlign: 'center' }}>
        This is a computer-generated voucher.
      </div>
    </div>
  );
}

// ─── Main Form ─────────────────────────────────────────────────────────
function ExpendituresForm({ expenditureId, onRefreshData = () => {}, beneficiariesList = [], purposesList = [] }) {
  const [form] = Form.useForm();
  const [orgForm] = Form.useForm();

  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [invoiceFileList, setInvoiceFileList] = useState([]);

  // Line items
  const [lineItems, setLineItems] = useState([newItem(1)]);
  const [nextKey, setNextKey] = useState(2);

  // Org settings (from database)
  const [orgSettings, setOrgSettings] = useState(DEFAULT_ORG);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Live preview values (mirror of form field values)
  const [previewValues, setPreviewValues] = useState({});

  // Budget check state
  const [budgetInfo, setBudgetInfo] = useState(null);   // null | { found, allocated, spent, remaining, max_per_entry }
  const [budgetLoading, setBudgetLoading] = useState(false);

  useEffect(() => {
    loadOrgSettings();
    if (expenditureId) {
      setIsUpdateMode(true);
      fetchExpenditureDetails();
    } else {
      form.resetFields();
      setPreviewValues({});
      setLineItems([newItem(1)]);
      setNextKey(2);
      setFileList([]);
      setInvoiceFileList([]);
    }
  }, [expenditureId]);

  // ── Budget check ──────────────────────────────────────────────────────────────
  const checkBudget = useCallback(async (purposeId, txnDate) => {
    if (!purposeId || !txnDate) { setBudgetInfo(null); return; }

    const d = txnDate.toDate ? txnDate.toDate() : new Date(txnDate);
    const y = d.getFullYear();
    const fy = d.getMonth() >= 3
      ? `${y}-${String(y+1).slice(-2)}`
      : `${y-1}-${String(y).slice(-2)}`;

    setBudgetLoading(true);
    try {
      const res = await axios.get(
        `${baseUrl}/budget-allocations/check?purpose_id=${purposeId}&fiscal_year=${fy}`,
        { headers: authHeaders() }
      );
      setBudgetInfo(res.data.status ? res.data : null);
    } catch (_) {
      setBudgetInfo(null);
    } finally {
      setBudgetLoading(false);
    }
  }, []);

  const loadOrgSettings = async () => {
    try {
      const res = await axios.get(`${baseUrl}/org-settings`, { headers: authHeaders() });
      if (res.data.status && res.data.result && res.data.result.name) {
        setOrgSettings({ ...DEFAULT_ORG, ...res.data.result });
      }
    } catch (_) {}
  };

  const fetchExpenditureDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/expenditures/read/${expenditureId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (res.status === 200) {
        const d = res.data.result;
        const vals = {
          ...d,
          txn_date: d.txn_date ? moment(d.txn_date) : null,
          cheque_date: d.cheque_date ? moment(d.cheque_date) : null,
        };
        form.setFieldsValue(vals);
        setPreviewValues(vals);

        if (d.line_items) {
          try {
            const saved = JSON.parse(d.line_items);
            if (Array.isArray(saved) && saved.length > 0) {
              setLineItems(saved.map((it, i) => ({ ...it, key: i + 1 })));
              setNextKey(saved.length + 1);
              return;
            }
          } catch (_) {}
        }
        // Legacy: build a single item from amount
        setLineItems([{ key: 1, description: d.remarks || 'Expenditure', qty: 1, rate: parseFloat(d.amount || 0), taxPct: 0 }]);
        setNextKey(2);
      }
    } catch {
      notification.error({ message: 'Error', description: 'Failed to fetch expenditure details', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  // Line item helpers
  const addLineItem = () => {
    setLineItems(prev => [...prev, newItem(nextKey)]);
    setNextKey(k => k + 1);
  };
  const removeLineItem = (key) => setLineItems(prev => prev.filter(i => i.key !== key));
  const updateLineItem = (key, field, value) =>
    setLineItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await axios.post(`${baseUrl}/org-settings/upload-logo`, fd, { headers: authHeaders() });
      if (res.data.status) {
        setOrgSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
        notification.success({ message: 'Logo uploaded', placement: 'topRight' });
      }
    } catch {
      notification.error({ message: 'Logo upload failed', placement: 'topRight' });
    } finally {
      setUploadingLogo(false);
    }
    return false; // prevent antd auto-upload
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${baseUrl}/org-settings/logo`, { headers: authHeaders() });
      setOrgSettings(prev => ({ ...prev, logo_url: null }));
      notification.success({ message: 'Logo removed', placement: 'topRight' });
    } catch {
      notification.error({ message: 'Failed to remove logo', placement: 'topRight' });
    }
  };

  // Save org settings to database
  const saveOrgSettings = async (values) => {
    setSavingOrg(true);
    try {
      const res = await axios.put(`${baseUrl}/org-settings`, values, { headers: authHeaders() });
      if (res.data.status) {
        setOrgSettings({ ...DEFAULT_ORG, ...res.data.result });
        setOrgModalOpen(false);
        notification.success({ message: 'Organization settings saved', placement: 'topRight' });
      }
    } catch (err) {
      notification.error({
        message: 'Error',
        description: err.response?.data?.message || 'Failed to save settings',
        placement: 'topRight'
      });
    } finally {
      setSavingOrg(false);
    }
  };

  const { subtotal, taxTotal, grandTotal } = computeTotals(lineItems);

  const onFinish = async (values) => {
    // Validate against purpose max_amount_limit
    if (budgetInfo?.found && budgetInfo.max_per_entry && grandTotal > budgetInfo.max_per_entry) {
      notification.error({
        message: 'Exceeds Purpose Limit',
        description: `This purpose has a per-entry limit of ₹${budgetInfo.max_per_entry.toLocaleString('en-IN')}. Your total is ₹${grandTotal.toLocaleString('en-IN')}.`,
        placement: 'topRight',
        duration: 0,
      });
      return;
    }
    // Warn (not block) if budget is exceeded
    if (budgetInfo?.found && grandTotal > budgetInfo.remaining) {
      const proceed = window.confirm(
        `⚠️ Budget Warning\n\nRemaining budget for this purpose: ₹${budgetInfo.remaining.toLocaleString('en-IN')}\nYour expenditure: ₹${grandTotal.toLocaleString('en-IN')}\n\nThis will exceed the budget. Do you want to continue?`
      );
      if (!proceed) return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('txn_date',        values.txn_date       ? values.txn_date.format('YYYY-MM-DD')    : '');
      fd.append('cheque_date',     values.cheque_date    ? values.cheque_date.format('YYYY-MM-DD') : '');
      fd.append('beneficiary_id',  values.beneficiary_id ?? '');
      fd.append('purpose_id',      values.purpose_id     ?? '');
      fd.append('amount',          subtotal.toFixed(2));
      fd.append('tax_amount',      taxTotal.toFixed(2));
      fd.append('currency',        values.currency        || 'INR');
      fd.append('payment_mode',    values.payment_mode    || '');
      fd.append('cheque_no',       values.cheque_no       || '');
      fd.append('utr_no',          values.utr_no          || '');
      fd.append('transaction_ref', values.transaction_ref || '');
      fd.append('invoice_no',      values.invoice_no      || '');
      fd.append('remarks',         values.remarks         || '');
      fd.append('internal_notes',  values.internal_notes  || '');
      fd.append('approval_status', values.approval_status || 'Pending');
      fd.append('line_items',      JSON.stringify(lineItems));

      if (fileList.length > 0 && fileList[0].originFileObj) fd.append('file', fileList[0].originFileObj);
      invoiceFileList.forEach(f => { if (f.originFileObj) fd.append('multiFiles', f.originFileObj); });

      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const response = isUpdateMode
        ? await axios.patch(`${baseUrl}/expenditures/update/${expenditureId}`, fd, { headers })
        : await axios.post(`${baseUrl}/expenditures/create`, fd, { headers });

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode ? 'Expenditure updated!' : 'Expenditure created!',
          placement: 'topRight'
        });
        onRefreshData();
        if (!isUpdateMode) {
          form.resetFields();
          setPreviewValues({});
          setLineItems([newItem(1)]);
          setNextKey(2);
          setFileList([]);
          setInvoiceFileList([]);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const sectionLabel = (title) => (
    <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 12, borderColor: '#d0dce8', margin: '12px 0 8px' }}>
      {title}
    </Divider>
  );

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 116px)', overflow: 'hidden' }}>

      {/* ── LEFT: Form Panel ── */}
      <div style={{ flex: '0 0 56%', overflowY: 'auto', paddingRight: 4, paddingBottom: 24 }}>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
          size="small"
          initialValues={{ currency: 'INR', approval_status: 'Pending' }}
          onValuesChange={(changed, all) => {
            setPreviewValues(all);
            if (changed.purpose_id || changed.txn_date) {
              checkBudget(all.purpose_id, all.txn_date);
            }
          }}
        >
          {/* Org Settings Bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#f0f4ff', border: '1px solid #c9d4f0', borderRadius: 6,
            padding: '8px 12px', marginBottom: 4
          }}>
            <div style={{ overflow: 'hidden' }}>
              <Text strong style={{ fontSize: 12, color: '#7a0000', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {orgSettings.name}
              </Text>
              {orgSettings.address && (
                <Text type="secondary" style={{ fontSize: 10 }}>{orgSettings.address}</Text>
              )}
            </div>
            <Button size="small" icon={<SettingOutlined />}
              onClick={() => { orgForm.setFieldsValue(orgSettings); setOrgModalOpen(true); }}
              style={{ marginLeft: 8, flexShrink: 0 }}>
              Edit Org Info
            </Button>
          </div>

          {sectionLabel('Transaction')}
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Date" name="txn_date" rules={[{ required: true, message: 'Required' }]}>
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Invoice No." name="invoice_no">
                <Input placeholder="e.g. INV-001 (auto if blank)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Beneficiary" name="beneficiary_id" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select beneficiary" showSearch optionFilterProp="children">
                  {beneficiariesList.map(b => (
                    <Option key={b.id} value={b.id}>{b.name} ({b.type})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Purpose" name="purpose_id" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select purpose" showSearch optionFilterProp="children">
                  {purposesList.map(p => (
                    <Option key={p.id} value={p.id}>{p.name} ({p.code})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ── Budget Info Box ── */}
          {budgetLoading && (
            <div style={{ textAlign: 'center', padding: '6px 0', color: '#888', fontSize: 12 }}>
              <Spin size="small" /> Checking budget...
            </div>
          )}
          {!budgetLoading && budgetInfo && budgetInfo.found && (
            <div style={{
              marginBottom: 10, padding: '10px 14px',
              borderRadius: 7, border: '1px solid #d9d9d9', background: '#fafcff',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', marginBottom: 6 }}>
                Budget — {budgetInfo.purpose_name}
              </div>
              <Progress
                percent={Math.min(100, Math.round((budgetInfo.spent / budgetInfo.allocated) * 100))}
                size="small"
                strokeColor={
                  budgetInfo.spent / budgetInfo.allocated > 0.9 ? '#ff4d4f' :
                  budgetInfo.spent / budgetInfo.allocated > 0.7 ? '#fa8c16' : '#52c41a'
                }
                format={p => `${p}% used`}
              />
              <Row gutter={16} style={{ marginTop: 6 }}>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#888' }}>Allocated</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#1890ff' }}>
                    ₹{budgetInfo.allocated.toLocaleString('en-IN')}
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#888' }}>Spent</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#fa8c16' }}>
                    ₹{budgetInfo.spent.toLocaleString('en-IN')}
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#888' }}>Remaining</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: budgetInfo.remaining > 0 ? '#52c41a' : '#ff4d4f' }}>
                    ₹{budgetInfo.remaining.toLocaleString('en-IN')}
                  </div>
                </Col>
              </Row>
              {budgetInfo.max_per_entry && (
                <div style={{ fontSize: 10, color: '#888', marginTop: 6, textAlign: 'center' }}>
                  Per-entry limit: <strong>₹{budgetInfo.max_per_entry.toLocaleString('en-IN')}</strong>
                </div>
              )}
              {grandTotal > 0 && grandTotal > budgetInfo.remaining && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8, fontSize: 11 }}
                  message={`Your current total ₹${grandTotal.toLocaleString('en-IN')} exceeds remaining budget by ₹${(grandTotal - budgetInfo.remaining).toLocaleString('en-IN')}`}
                />
              )}
            </div>
          )}
          {!budgetLoading && budgetInfo && !budgetInfo.found && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 10, fontSize: 11 }}
              message="No budget allocated for this purpose and fiscal year. You can still save, but this won't be tracked against a budget."
            />
          )}

          {sectionLabel('Line Items')}

          {/* Items Table Header */}
          <div style={{
            background: '#7a0000', color: '#fff',
            borderRadius: '6px 6px 0 0', padding: '5px 8px',
          }}>
            <Row gutter={4}>
              <Col span={9}><Text style={{ color: '#fff', fontSize: 11 }}>Description</Text></Col>
              <Col span={3}><Text style={{ color: '#fff', fontSize: 11 }}>Qty</Text></Col>
              <Col span={5}><Text style={{ color: '#fff', fontSize: 11 }}>Rate (₹)</Text></Col>
              <Col span={3}><Text style={{ color: '#fff', fontSize: 11 }}>Tax%</Text></Col>
              <Col span={4}><Text style={{ color: '#fff', fontSize: 11 }}>Total</Text></Col>
            </Row>
          </div>

          {lineItems.map((item, idx) => {
            const s = (item.qty || 0) * (item.rate || 0);
            const t = s * (item.taxPct || 0) / 100;
            return (
              <div key={item.key} style={{
                border: '1px solid #dde3ed', borderTop: 'none',
                padding: '5px 8px',
                background: idx % 2 === 0 ? '#fff' : '#fafcff',
                borderRadius: idx === lineItems.length - 1 ? '0 0 6px 6px' : 0,
              }}>
                <Row gutter={4} align="middle">
                  <Col span={9}>
                    <Input
                      size="small"
                      placeholder={`Item ${idx + 1} description`}
                      value={item.description}
                      onChange={e => updateLineItem(item.key, 'description', e.target.value)}
                    />
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      size="small" style={{ width: '100%' }} min={1}
                      value={item.qty}
                      onChange={v => updateLineItem(item.key, 'qty', v)}
                    />
                  </Col>
                  <Col span={5}>
                    <InputNumber
                      size="small" style={{ width: '100%' }} min={0}
                      value={item.rate}
                      onChange={v => updateLineItem(item.key, 'rate', v)}
                      formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => v.replace(/,/g, '')}
                    />
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      size="small" style={{ width: '100%' }} min={0} max={100}
                      value={item.taxPct}
                      onChange={v => updateLineItem(item.key, 'taxPct', v)}
                      formatter={v => `${v}%`}
                      parser={v => v.replace('%', '')}
                    />
                  </Col>
                  <Col span={3}>
                    <Text style={{ fontSize: 11, fontWeight: 600, color: '#7a0000' }}>
                      {inr(s + t)}
                    </Text>
                  </Col>
                  <Col span={1}>
                    {lineItems.length > 1 && (
                      <Button
                        type="text" danger size="small" icon={<DeleteOutlined />}
                        onClick={() => removeLineItem(item.key)}
                        style={{ padding: '0 2px' }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            );
          })}

          <Button
            icon={<PlusOutlined />} size="small" onClick={addLineItem}
            style={{ marginTop: 6, width: '100%', borderStyle: 'dashed', color: '#7a0000', borderColor: '#7a0000' }}
          >
            Add Line Item
          </Button>

          {/* Totals Summary */}
          <div style={{
            marginTop: 8, background: '#f7f9fc',
            border: '1px solid #dde3ed', borderRadius: 6, padding: '8px 12px'
          }}>
            {[
              ['Subtotal', subtotal, false],
              ['Tax Total', taxTotal, false],
              ['Grand Total', grandTotal, true],
            ].map(([label, val, bold]) => (
              <Row key={label} style={{ marginBottom: 2 }}>
                <Col span={16} style={{ textAlign: 'right', paddingRight: 12 }}>
                  <Text type={bold ? undefined : 'secondary'} strong={bold} style={{ fontSize: bold ? 13 : 11 }}>
                    {label}:
                  </Text>
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <Text strong={bold} style={{ fontSize: bold ? 13 : 11, color: bold ? '#7a0000' : undefined }}>
                    {inr(val)}
                  </Text>
                </Col>
              </Row>
            ))}
          </div>

          {sectionLabel('Payment Details')}
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Payment Mode" name="payment_mode" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select mode">
                  <Option value="Cheque">Cheque</Option>
                  <Option value="NEFT">NEFT</Option>
                  <Option value="RTGS">RTGS</Option>
                  <Option value="UPI">UPI</Option>
                  <Option value="Cash">Cash</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="UTR / Ref Number" name="utr_no">
                <Input placeholder="UTR number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Cheque Number" name="cheque_no">
                <Input prefix={<BankOutlined />} placeholder="Cheque number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Cheque Date" name="cheque_date">
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Transaction Ref" name="transaction_ref">
                <Input placeholder="Transaction reference" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Status" name="approval_status">
                <Select>
                  <Option value="Pending">Pending</Option>
                  <Option value="Approved">Approved</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Remarks" name="remarks">
                <TextArea placeholder="Visible on voucher" rows={2} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Internal Notes" name="internal_notes">
                <TextArea placeholder="Internal only" rows={2} />
              </Form.Item>
            </Col>
          </Row>

          {sectionLabel('Documents')}
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Proof Document">
                <Upload fileList={fileList} onChange={info => setFileList(info.fileList)} beforeUpload={() => false} maxCount={1}>
                  <Button icon={<UploadOutlined />} type="dashed" style={{ width: '100%' }} size="small">
                    Upload Proof
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Invoice Documents (multiple)">
                <Upload fileList={invoiceFileList} onChange={info => setInvoiceFileList(info.fileList)} beforeUpload={() => false} multiple>
                  <Button icon={<UploadOutlined />} type="dashed" style={{ width: '100%' }} size="small">
                    Upload Invoices
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
            <Button htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large"
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', minWidth: 180 }}>
              {isUpdateMode ? 'Update Expenditure' : 'Save Expenditure'}
            </Button>
          </div>
        </Form>
      </div>

      {/* ── RIGHT: Live Preview Panel ── */}
      <div style={{ flex: '0 0 44%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          background: '#7a0000', color: '#fff',
          padding: '8px 14px', borderRadius: '8px 8px 0 0',
          fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />
            Live Preview
          </span>
          <Button
            size="small" icon={<PrinterOutlined />}
            onClick={() => {
              const content = document.getElementById('invoice-preview-content').innerHTML;
              const w = window.open('', '_blank', 'width=900,height=700');
              w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Voucher</title>
                <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;background:#fff}
                @media print{@page{margin:10mm}}</style></head>
                <body>${content}</body></html>`);
              w.document.close();
              w.focus();
              setTimeout(() => { w.print(); }, 400);
            }}
            style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.3)', color: '#fff', fontSize: 11 }}
          >
            Print
          </Button>
        </div>
        <div id="invoice-preview-content">
          <InvoicePreview
            org={orgSettings}
            values={previewValues}
            items={lineItems}
            beneficiariesList={beneficiariesList}
            purposesList={purposesList}
          />
        </div>
      </div>

      {/* Org Settings Modal */}
      <Modal
        title={<span style={{ color: '#7a0000', fontWeight: 700 }}>Organization Settings</span>}
        open={orgModalOpen}
        onCancel={() => setOrgModalOpen(false)}
        onOk={() => orgForm.submit()}
        okText="Save Settings"
        okButtonProps={{ style: { background: '#7a0000', borderColor: '#7a0000' }, loading: savingOrg }}
        width={500}
        destroyOnClose
      >
        <Form form={orgForm} layout="vertical" onFinish={saveOrgSettings} size="small">
          <Form.Item label="Organization Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Shri Kheteshwar Brahmdham Tirth, Asotra" />
          </Form.Item>
          <Form.Item label="Sub-organization / Tagline" name="subtitle">
            <Input placeholder="e.g. ABRSVS – Samaj Shiksha Nidhi Samarpan Yojana" />
          </Form.Item>
          <Form.Item label="Address" name="address">
            <TextArea rows={2} placeholder="Full address" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="Phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Email" name="email">
                <Input placeholder="Email address" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="GSTIN (optional)" name="gst">
            <Input placeholder="GST number" />
          </Form.Item>
        </Form>

        {/* Logo Upload — outside orgForm so it saves immediately */}
        <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#7a0000', marginBottom: 8 }}>
            Organization Logo
          </div>
          {orgSettings.logo_url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img
                src={orgSettings.logo_url}
                alt="org-logo"
                style={{ height: 60, width: 'auto', objectFit: 'contain', border: '1px solid #eee', borderRadius: 6, padding: 4, background: '#fafafa' }}
              />
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Logo uploaded</div>
                <Button
                  size="small" danger icon={<DeleteOutlined />}
                  onClick={handleLogoDelete}
                >
                  Remove Logo
                </Button>
              </div>
            </div>
          ) : (
            <Upload
              beforeUpload={handleLogoUpload}
              accept="image/png,image/jpeg,image/svg+xml"
              showUploadList={false}
            >
              <Button
                icon={uploadingLogo ? null : <PictureOutlined />}
                loading={uploadingLogo}
                type="dashed"
                style={{ borderColor: '#7a0000', color: '#7a0000' }}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload Logo (PNG / JPG / SVG)'}
              </Button>
            </Upload>
          )}
        </div>

        <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
          These settings are saved in the database — all users will see the same organization info on vouchers and PDFs.
        </div>
      </Modal>
    </div>
  );
}

export default ExpendituresForm;
